/**
 * Builds the `sameAs` list for a directory item's structured data.
 *
 * `sameAs` tells search engines that the ArchiPro profile and the business's own web presence
 * describe one entity, which is what lets brand and authority signals consolidate rather than
 * compete. Only absolute http(s) URLs qualify: the stored fields are free text that can hold bare
 * handles, notes, or relative paths, and an unresolvable value in `sameAs` weakens the claim rather
 * than strengthening it. Duplicates collapse because the same profile is often stored twice under
 * different casing or with a trailing slash.
 */
export function buildSameAs(links: Array<string | null | undefined>): string[] {
    const urls = new Set<string>();

    for (const link of links) {
        const url = normaliseExternalUrl(link);
        if (url) {
            urls.add(url);
        }
    }

    return [...urls];
}

/** Returns the canonical absolute form of an http(s) URL, or undefined when it is not one. */
export function normaliseExternalUrl(
    value: string | null | undefined,
): string | undefined {
    const trimmed = value?.trim();

    if (!trimmed) {
        return undefined;
    }

    try {
        const url = new URL(trimmed);
        return url.protocol === 'http:' || url.protocol === 'https:'
            ? url.toString()
            : undefined;
    } catch {
        return undefined;
    }
}
